import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Target,
  Eye,
  BookOpen,
  Users,
  Shield,
  Award,
  CheckCircle,
  Heart,
  Video,
  Stethoscope,
  Globe,
  Zap,
  TrendingUp,
  BarChart3,
  DollarSign,
  Building2,
  Clock,
  Activity
} from 'lucide-react';

const About = () => {
  const objectives = [
    "Support children's emotional, physical, and cognitive development",
    "Improve quality of life for families with limited resources",
    "Promote healthy habits: nutrition, hydration, exercise, hygiene, vaccination",
    "Engage families in early health-seeking behaviors",
    "Reduce disparities across rural vs. urban, poor vs. wealthy communities",
    "Enable early detection of illnesses through telehealth triage and checkups",
    "Lower long-term costs by preventing complications",
    "Build community trust in healthcare systems"
  ];

  const teamMembers = [
    {
      name: "Grant Zukel",
      role: "CEO & Founder",
      bio: "Grant Zukel is a CEO-operator across telecom, healthcare, energy, and agribusiness in Central and West Africa. A former U.S. Air Force intelligence professional, he pairs multilingual fluency (English, Japanese, Chinese), a philosopher's training, and fifth-generation Tai Chi lineage with calm, board-level execution. He has founded and scaled a tech startup, is planning global telemedicine as part of his overall goals to bring change to the world, and now runs a game studio, an investment group, and a high-growth consulting practice—driving telecom M&A and measurable technology rollouts. Grant also leads SynthAI, an AI materials-prediction project, and is developing first-of-its-kind graphene-concrete batteries for remote telecom towers.",
      image: "/New HeadShot-cropped.jpg"
    },
    {
      name: "Dr. Ajume H. Wingo",
      role: "Founder | Chief Relations Officer (CRO)",
      bio: "Dr. Ajume H. Wingo—a tenured CU Boulder political philosopher and Prince of Nso—serves as Founder, Chief Relations Officer, and Board Director of Global Telecom, where he converts top-tier alliances into durable, dignified outcomes. Trained at UC Berkeley and UW–Madison (MA/PhD) with fellowships at Harvard and Boston University, he has taught at UMass Boston, Clark, and Emerson. Author of Veil Politics in Liberal Democratic States and leading work on dignity and the \"shade of power,\" Wingo wields a uniquely global network across governments, regulators, universities, NGOs, and industry. He reliably unlocks doors—securing approvals, MOUs, funding, and execution—which directly advances AMHAS through ministry partnerships, hospital and university linkages, donor channels, and country-level clearances.",
      image: "/ajume-headshot.jpg"
    },
    {
      name: "Brenda Zukel",
      role: "Chief Operating Officer",
      bio: "Brenda Zukel is an executive leader with deep experience in organizational operations, financial strategy, and cross-functional management. She specializes in building efficient infrastructures, strengthening organizational clarity, and guiding teams through growth, change, and high-stakes decision-making. Her career spans leadership roles across operations, systems development, and national team coordination, where she has implemented scalable processes, improved workflow efficiency, and supported strategic initiatives at the executive level. Brenda is known for her decisive problem-solving, operational discipline, and ability to turn complex challenges into actionable solutions. In addition to her corporate leadership, Brenda is the owner and chef of The Banana Leaf, a Hmong-inspired fusion restaurant in Colorado. Her hands-on business ownership brings a grounded, people-centered perspective to her leadership style and enriches her approach to organizational development and community impact. Brenda's leadership is defined by clarity, adaptability, and a commitment to excellence—qualities that make her a strong contributor to mission-driven governance and strategic direction.",
      image: "/brenda-headshot.jpeg"
    },
    {
      name: "Fatoumata Kolley",
      role: "Investor | Chief Nurse",
      bio: "Fatoumata Kolley—AMHAS's lead angel investor and Director of Nursing—is a Gambian-born nurse practitioner in New Jersey with 10+ years of frontline experience. From U.S. hospitals to championing preventive child care in The Gambia, she pairs professional excellence with heartfelt purpose. Her grace, compassion, and resolve make her a cornerstone of AMHAS and an inspiration to all who work beside her.",
      image: "/nurse_kelly_hs.jpg"
    },
    {
      name: "Lawrence Kuhndze Njola",
      role: "General Manager",
      bio: "Lawrence Kuhndze Njolai is a research assistant to Dr/Professor Ajume H. Wingo. He hails from Nso in Cameroon, and holds the traditional Tav Jwim Nso title-signifying honor, discipline, and service with decades of experience as a Surgical Technologist in General surgery, Orthopedic Surgery, Neuro and Spine surgery, who has led numerous trainings at the Banso Baptist Hospital (BBH) and Cameroon Baptist Convention Health Services Hospitals. He also served with St. John of God Catholic Hospital in Yassa Douala, where he recently left to occupy the present position. He pairs deep clinical experience with community leadership. He is the Founder and President of Bamdzeng Young Farmers' Cooperative Society with Board of Directors, demonstrating a commitment to local economic empowerment. What makes Lawrence Kuhndze Njolai AMHAS definitive governance staff member is the powerful synergy between his cultural authority and professional breadth. He serves as Interim Station Manager of HelenKriss FM Radio Kumbo 96.0 MHtz, He coordinated the African Culture Revival Society of Shasha Foundation and founded the Nso Information and Culture Centre (NICC), devoting his efforts to heritage preservation. Beyond these roles, he maintains a deep connection to the land and the arts—through farming and music. His goal is to transform healthcare assurance for children across Africa.",
      image: "/nsoman_upscal_hs.jpg"
    },
    {
      name: "Dr. Kent Northcote",
      role: "Senior Telehealth Expert",
      bio: "Board-Certified Emergency Medicine Physician since 2002; Telehealth public speaker and patient advocacy leader emphasizing critical thinking since 2004; Shareholder/Partner Physician at Ergentus; Board Member and Chairman of the Good Samaritan Hospital Ethics Committee; Former Senior Medical Director at MDLive.",
      image: "/dr-kent-northwood.webp"
    }
  ];

  const complianceItems = [
    "Registered with Cameroon & The Gambia Ministries of Health",
    "Licensed insurance provider",
    "ISO 9001:2015 Quality Management System",
    "Regular third-party audits and compliance reviews",
    "Data protection and privacy compliance"
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_front_buildign.png" 
            alt="AMHAS Urgent Care" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">About AMHAS</h1>
              <p className="text-xl md:text-2xl text-primary-100 mb-8">
                Building a healthier future for children through accessible, affordable, and preventive healthcare.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                  <Heart size={20} className="text-yellow-300" />
                  <span className="text-sm font-semibold">World-Class Care</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                  <Stethoscope size={20} className="text-yellow-300" />
                  <span className="text-sm font-semibold">24/7 Access</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-lg px-4 py-2">
                  <Users size={20} className="text-yellow-300" />
                  <span className="text-sm font-semibold">Community Focused</span>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-lg overflow-hidden shadow-2xl">
                <img 
                  src="/urgentcare_doctor_Care_1.png" 
                  alt="AMHAS Healthcare" 
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/50 to-transparent"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="card p-8 overflow-hidden"
            >
              <div className="flex items-center mb-6">
                <div className="w-20 h-20 bg-primary-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <Target size={40} className="text-primary-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
              </div>
              <div className="relative mb-6 rounded-lg overflow-hidden">
                <img 
                  src="/urgentcare_telehealth_classrom.png" 
                  alt="AI Telehealth in Classroom" 
                  className="w-full h-48 object-cover"
                />
              </div>
              <p className="text-gray-600 text-lg leading-relaxed">
                To provide preventive and primary healthcare to schoolchildren (ages 2-18) through world-class urgent care centers 
                and AI-powered telehealth. Students can receive care at our vertically integrated urgent care facilities with 100% 
                coverage, or via AI + Western telehealth directly in the classroom. At school, staff record vitals and symptoms; AI 
                proposes an initial diagnosis and care plan which a Western board‑certified doctor reviews and finalizes. Our urgent 
                care centers are being established while we scale AI and telehealth technology deployments. Prevention remains at the 
                core—because preventing illness is cheaper, safer, and life‑saving.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="card p-8 overflow-hidden"
            >
              <div className="flex items-center mb-6">
                <div className="w-20 h-20 bg-accent-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <Eye size={40} className="text-accent-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900">Our Vision</h2>
              </div>
              <div className="relative mb-6 rounded-lg overflow-hidden">
                <img 
                  src="/african-school-kid.jpg" 
                  alt="Healthy Children" 
                  className="w-full h-48 object-cover"
                />
              </div>
              <p className="text-gray-600 text-lg leading-relaxed">
                To become Africa's leading preventive children's healthcare assurance service, setting standards of
                compassion, excellence, technology, and empathy in safeguarding the next generation. We envision a
                future where no child misses school or risks their life because healthcare was too far, too late,
                or too expensive.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center mb-4">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                <BookOpen size={32} className="text-primary-600" />
              </div>
              <h2 className="section-title mb-0">Our Story</h2>
            </div>
          </motion.div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="card p-6"
            >
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart size={32} className="text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">The Challenge</h3>
              <p className="text-gray-600 leading-relaxed">
                In many rural African communities, parents face impossible choices: delay treatment until illness is critical,
                spend beyond their means at private clinics, or turn to traditional remedies that may not fully address the issue.
                The result? Preventable illnesses like malaria, diarrhoea, and pneumonia keep children out of school, weaken
                their growth, and sometimes take their lives.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="card p-6"
            >
              <div className="relative mb-4 rounded-lg overflow-hidden">
                <img 
                  src="/urgentcare_front_buildign.png" 
                  alt="AMHAS Urgent Care Center" 
                  className="w-full h-48 object-cover"
                />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Our Solution</h3>
              <p className="text-gray-600 leading-relaxed">
                AMHAS was founded to break this cycle. We bring care to schools where children already are, and we're building 
                world-class urgent care centers to provide in-person care when needed. Students can receive care at our urgent care 
                facilities with 100% coverage, or via AI-powered telehealth consultation directly in the classroom.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="card p-6"
            >
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">The Impact</h3>
              <p className="text-gray-600 leading-relaxed">
                Our urgent care centers provide comprehensive services including lab work, radiology, pharmacy, and observation beds—all 
                at 100% coverage for enrolled students. This dual-pathway approach delivers timely, high‑quality care at a fraction of 
                typical costs. Healthy children are not just individuals—they are the foundation of strong, peaceful communities.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Guiding Proverbs */}
      <section className="relative section-padding bg-primary-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_doctor_greet_1.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-8">Guiding Proverbs</h2>
            <div className="space-y-6">
              <blockquote className="text-xl italic">
                "Guard a child's health today, and the village will shine tomorrow."
              </blockquote>
              <blockquote className="text-xl italic">
                "A child well cared for today becomes the light of the village tomorrow."
              </blockquote>
              <blockquote className="text-xl italic">
                "A child embraced by health and care brings light, not fire, to the village."
              </blockquote>
              <blockquote className="text-xl italic">
                "Health planted in childhood becomes the harvest of tomorrow."
              </blockquote>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Our Philosophy: Prevention + Technology</h2>
            <p className="section-subtitle">
              Combining traditional wisdom with modern healthcare solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: "Prevention First",
                description: "Checkups, education, sanitation, and safe water programs to prevent illness before it starts."
              },
              {
                icon: Video,
                title: "AI + Western Telehealth",
                description: "AI triage at school with Western doctor review, or receive care at our world-class urgent care centers."
              },
              {
                icon: Building2,
                title: "Urgent Care Centers",
                description: "World-class facilities with 100% coverage for students, serving communities 24/7 with full vertical integration."
              },
              {
                icon: DollarSign,
                title: "Affordability",
                description: `An annual premium of $28 USD (15,820 CFA / 2,030 GMD) is less than one clinic visit, making healthcare accessible.`
              },
              {
                icon: CheckCircle,
                title: "Transparency",
                description: "Simple coverage rules, no hidden costs, rapid claims processing for complete trust."
              },
              {
                icon: Users,
                title: "Community-Driven",
                description: "Schools, parents, and local leaders lead enrollment and outreach for sustainable impact."
              },
              {
                icon: Globe,
                title: "Global Standards",
                description: "Western board-certified physicians ensure quality care and international best practices."
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card p-6 text-center"
                >
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon size={32} className="text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Urgent Care Centers Section */}
      <section className="relative section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_front_buildign.png" 
            alt="AMHAS Urgent Care" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold mb-6">AMHAS Urgent Healthcare Centers</h2>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              World-class facilities engineered from the ground up to deliver Western-standard healthcare
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-8"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mr-4">
                  <Building2 size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold">Our Centers</h3>
              </div>
              <p className="text-lg text-primary-100 mb-6">
                Our world-class urgent care centers are engineered from the ground up to deliver Western-standard healthcare 
                in a repeatable, scalable, and vertically integrated model. Each facility is designed with world-class engineering 
                standards, allowing us to deploy clinics rapidly while maintaining exceptional quality and low operating costs.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-yellow-300 mt-1 flex-shrink-0" />
                  <span className="text-primary-100">24 hours a day, 365 days a year operation</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-yellow-300 mt-1 flex-shrink-0" />
                  <span className="text-primary-100">Full vertical integration: pharmacy, lab, radiology, observation beds</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-yellow-300 mt-1 flex-shrink-0" />
                  <span className="text-primary-100">U.S.-licensed contractor review for quality assurance</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-yellow-300 mt-1 flex-shrink-0" />
                  <span className="text-primary-100">Biometric ID verification for instant coverage validation</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white/10 backdrop-blur-sm rounded-lg p-8"
            >
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mr-4">
                  <Users size={32} className="text-white" />
                </div>
                <h3 className="text-2xl font-bold">Serving Students & Community</h3>
              </div>
              <p className="text-lg text-primary-100 mb-6">
                Our urgent care centers provide 100% coverage for enrolled students (ages 2-18) at no additional cost beyond 
                the annual enrollment fee. These facilities also serve the surrounding community at low-cost premium rates, 
                creating additional revenue streams that strengthen long-term sustainability while ensuring every child receives 
                the care they need.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-yellow-300 mt-1 flex-shrink-0" />
                  <span className="text-primary-100">Students: 100% coverage for all services</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-yellow-300 mt-1 flex-shrink-0" />
                  <span className="text-primary-100">Community: Low-cost premium services</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-yellow-300 mt-1 flex-shrink-0" />
                  <span className="text-primary-100">Same world-class facilities and services for all</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-yellow-300 mt-1 flex-shrink-0" />
                  <span className="text-primary-100">Community revenue supports facility sustainability</span>
                </li>
              </ul>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link to="/urgent-care" className="btn-secondary text-lg px-8 py-4 inline-block">
              Learn More About Our Urgent Care Centers
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Objectives */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Our Objectives</h2>
            <p className="section-subtitle">
              Clear goals that guide our work and measure our impact
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {objectives.map((objective, index) => (
              <motion.div
                key={objective}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-6"
              >
                <div className="flex items-start space-x-3">
                  <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{objective}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team & Governance */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Team & Governance</h2>
            <p className="section-subtitle">
              Experienced professionals dedicated to improving child health
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-6 text-center"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-primary-600 to-accent-600 rounded-full mx-auto mb-4 flex items-center justify-center overflow-hidden">
                  {member.image ? (
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Users size={32} className="text-white" />
                  )}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{member.name}</h3>
                <p className="text-primary-600 font-semibold mb-3">{member.role}</p>
                <p className="text-gray-600 text-sm">{member.bio}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="card p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Governance & Team</h3>
              <p className="text-gray-600 leading-relaxed">
                AMHAS operates with compliance, transparency, and accountability. Our Board & Advisors include experts in
                public health, finance, education, and governance. We operate through vertically integrated urgent care facilities
                across target regions, with Telehealth Doctors who are Western board-certified physicians
                ensuring quality and second opinions. Our Data Systems track enrollment, claims, and outcomes for schools,
                investors, and ministries of health.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Compliance */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Compliance & Quality Assurance</h2>
            <p className="section-subtitle">
              Committed to the highest standards of quality and regulatory compliance
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="card p-8 max-w-4xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {complianceItems.map((item, index) => (
                <div key={item} className="flex items-center space-x-3">
                  <Shield size={20} className="text-accent-600 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="flex items-center justify-center space-x-2 text-gray-600">
                <Award size={20} className="text-primary-600" />
                <span className="font-semibold">Regular audits and compliance reviews ensure we maintain the highest standards</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final Call-to-Action */}
      <section className="relative section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_telehealth_classrom.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Together, we can build a future where no child is left behind.</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Join us in creating a healthcare system that works for every child, every family, and every community.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/student-enrollment" className="btn-secondary text-lg px-8 py-4">
                Enroll Your Child
              </Link>
              <Link to="/urgent-care" className="btn-secondary text-lg px-8 py-4">
                Learn About Urgent Care
              </Link>
              <Link to="/investors" className="btn-secondary text-lg px-8 py-4">
                Invest in AMHAS
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
